export function formatDate(date: string | Date, options?: Intl.DateTimeFormatOptions): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options,
  }
  
  return new Intl.DateTimeFormat('nl-NL', defaultOptions).format(dateObj)
}

export function formatShortDate(date: string | Date): string {
  return formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function formatRelativeDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffInMillis = now.getTime() - dateObj.getTime()
  const diffInDays = Math.floor(diffInMillis / (1000 * 60 * 60 * 24))
  
  if (diffInDays === 0) {
    return 'Vandaag'
  } else if (diffInDays === 1) {
    return 'Gisteren'
  } else if (diffInDays < 7) {
    return `${diffInDays} dagen geleden`
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7)
    return weeks === 1 ? '1 week geleden' : `${weeks} weken geleden`
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30)
    return months === 1 ? '1 maand geleden' : `${months} maanden geleden`
  } else {
    const years = Math.floor(diffInDays / 365)
    return years === 1 ? '1 jaar geleden' : `${years} jaar geleden`
  }
}

export function formatEcli(ecli: string): string {
  return ecli.toUpperCase()
}

export function formatCaseNumber(caseNumber: string): string {
  return caseNumber.replace(/\s+/g, ' ').trim()
}

export function formatInstantieNiveau(niveau: string): string {
  const niveauMap: Record<string, string> = {
    hoge_raad: 'Hoge Raad',
    gerechtshof: 'Gerechtshof',
    rechtbank: 'Rechtbank',
    centrale_raad_van_beroep: 'Centrale Raad van Beroep',
    college_van_beroep_voor_het_bedrijfsleven: 'College van Beroep voor het bedrijfsleven',
    raad_van_state: 'Raad van State',
    anders: 'Anders',
  }
  
  return niveauMap[niveau] || niveau
}

export function truncateText(text: string, maxLength: number = 150): string {
  if (text.length <= maxLength) return text
  
  const truncated = text.substring(0, maxLength)
  const lastSpaceIndex = truncated.lastIndexOf(' ')
  
  if (lastSpaceIndex > 0) {
    return truncated.substring(0, lastSpaceIndex) + '...'
  }
  
  return truncated + '...'
}

export function highlightSearchTerms(text: string, searchTerms: string[]): string {
  if (!searchTerms || searchTerms.length === 0) return text

  let highlightedText = text

  searchTerms.forEach(term => {
    if (term.length < 2) return

    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedTerm})`, 'gi')
    highlightedText = highlightedText.replace(regex, '<mark>$1</mark>')
  })

  return highlightedText
}

export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return `${count} ${singular}`
  return `${count} ${plural || singular + 'en'}`
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/--+/g, '-')
    .trim()
    .replace(/^-+|-+$/g, '')
}